import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { type WeightUnit } from '../../hooks/useWeightUnit';

interface WeightInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number | undefined | null; // Value in kg
  onChange: (valueKg: number | undefined) => void;
  unit: WeightUnit;
  showUnitLabel?: boolean;
}

export const WeightInput: React.FC<WeightInputProps> = ({
  value,
  onChange,
  unit,
  showUnitLabel = true,
  className,
  onBlur,
  onKeyDown: onKeyDownProp,
  onPaste: onPasteProp,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const formattedValue = useMemo(() => {
    if (value === undefined || value === null || isNaN(value) || value === 0) return '';
    if (unit === 'kg') return value.toFixed(3);
    return String(Math.round(value * 1000));
  }, [value, unit]);

  const parseDisplayToNumber = (raw: string) => {
    const normalized = raw.replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const sanitizeDisplayValue = (raw: string) => {
    if (raw === '') return '';

    if (unit === 'gram') {
      return raw.replace(/[^\d]/g, '');
    }

    const kept = raw.replace(/[^\d.,]/g, '').replace(/,/g, '');
    const firstDotIndex = kept.indexOf('.');
    if (firstDotIndex === -1) return kept;
    const integerPart = kept.slice(0, firstDotIndex);
    const decimalRaw = kept.slice(firstDotIndex + 1).replace(/\./g, '');
    const decimalPart = decimalRaw.slice(0, 3);
    return `${integerPart}.${decimalPart}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValRaw = e.target.value;
    const newVal = sanitizeDisplayValue(newValRaw);
    setDisplayValue(newVal);

    if (newVal === '') {
      onChange(undefined);
      return;
    }

    const parsed = parseDisplayToNumber(newVal);
    if (parsed === undefined) return;

    if (unit === 'kg') {
      onChange(parsed);
      return;
    }
    onChange(parsed / 1000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDownProp?.(e);
    if (e.defaultPrevented) return;

    if (e.ctrlKey || e.metaKey || e.altKey) return;

    const key = e.key;
    const isNavigation =
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'Home' ||
      key === 'End' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown';
    if (isNavigation) return;

    if (key.length !== 1) return;

    if (/\d/.test(key)) {
      if (unit === 'kg') {
        const el = e.currentTarget;
        const value = el.value;
        const dotIndex = value.indexOf('.');
        if (dotIndex !== -1) {
          const selectionStart = el.selectionStart ?? value.length;
          const selectionEnd = el.selectionEnd ?? value.length;
          const hasSelection = selectionEnd > selectionStart;
          const decimalLength = value.slice(dotIndex + 1).length;
          const typingInDecimal = selectionStart > dotIndex;
          if (typingInDecimal && !hasSelection && decimalLength >= 3) {
            e.preventDefault();
            return;
          }
        }
      }
      return;
    }

    if (unit === 'kg' && key === '.') {
      const el = e.currentTarget;
      const next =
        el.value.slice(0, el.selectionStart ?? el.value.length) +
        '.' +
        el.value.slice(el.selectionEnd ?? el.value.length);
      if ((next.match(/\./g) ?? []).length <= 1) return;
    }

    e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    onPasteProp?.(e);
    if (e.defaultPrevented) return;

    const text = e.clipboardData.getData('text');
    const el = e.currentTarget;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const nextRaw = el.value.slice(0, start) + text + el.value.slice(end);
    const next = sanitizeDisplayValue(nextRaw);

    e.preventDefault();

    setIsEditing(true);
    setDisplayValue(next);

    if (next === '') {
      onChange(undefined);
      return;
    }

    const parsed = parseDisplayToNumber(next);
    if (parsed === undefined) return;
    onChange(unit === 'kg' ? parsed : parsed / 1000);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    if (onBlur) onBlur(e);
  };

  const unitLabel = unit === 'kg' ? 'kg' : 'g';

  return (
    <div className="relative w-full">
      <input
        {...props}
        type="text"
        inputMode={unit === 'kg' ? 'decimal' : 'numeric'}
        value={isEditing ? displayValue : formattedValue}
        onChange={(e) => {
          setIsEditing(true);
          handleChange(e);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={props.placeholder ?? (unit === 'kg' ? '0.000' : '0')}
        className={clsx(className, showUnitLabel && 'pr-10')}
      />
      {showUnitLabel && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 select-none text-xs text-gray-400">
          {unitLabel}
        </span>
      )}
    </div>
  );
};
